import { useTranslation } from 'react-i18next';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { Card, CardContent } from '@/components/ui-kit/card';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { useServerTable } from '@/hooks/use-server-table';
import { DEFAULT_OVERTIME_LEDGER_ORDER } from '../../graphql/queries';
import { useGetOvertimeBalance, useGetOvertimeLedger } from '../../hooks/use-overtime';
import { OvertimeLedgerPagingResult } from '../../types/overtime.types';
import { formatSecondsAsHoursMinutes, formatSignedHoursMinutes } from '../../utils/format-duration';
import { createOvertimeLedgerColumns } from '../overtime-ledger-columns/overtime-ledger-columns';

interface UserOvertimePanelProps {
  /** IAM user id of the employee. Reads are limited by the Overtime data rules (own rows, heads of
   * business their departments, admins all), so a row out of reach simply isn't returned. */
  userId: string;
}

/**
 * One employee's overtime: the balance totals and the ledger of every change, newest first (legacy
 * overtime-user-statistic + user-overtime-ledger-table). Used by the overtime manager's details page
 * and the own Overtime tab of My Attendance.
 */
export const UserOvertimePanel = ({ userId }: UserOvertimePanelProps) => {
  const { t } = useTranslation();
  const { pagination, order, columnWhere, tableProps } = useServerTable({
    pageSize: 15,
    defaultOrder: DEFAULT_OVERTIME_LEDGER_ORDER,
  });

  const { data: balance, isLoading: isBalanceLoading } = useGetOvertimeBalance(userId);
  const { data, isLoading, error } = useGetOvertimeLedger({
    userId,
    pageNo: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    filter: columnWhere,
    order,
  });
  const result = (data as { OvertimeLedger?: OvertimeLedgerPagingResult })?.OvertimeLedger;

  const stats = [
    {
      key: 'REMAINING_HOURS',
      value: formatSignedHoursMinutes(balance?.TotalRemainingOvertimeHourInSecond),
    },
    {
      key: 'TAKEN_HOURS',
      value: formatSecondsAsHoursMinutes(balance?.TotalTakenOvertimeHourInSecond),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      {/* Both stats always show, each one line: the value beside its title. */}
      <div className="grid grid-cols-1 gap-4 max-md:gap-2 md:grid-cols-2">
        {stats.map((stat) => (
          <Card key={stat.key}>
            <CardContent className="flex flex-row items-center justify-between gap-3 px-4 py-2.5">
              <span className="text-sm text-muted-foreground">{t(stat.key)}</span>
              {isBalanceLoading ? (
                <Skeleton className="h-6 w-20" />
              ) : (
                <span className="text-lg font-semibold">{stat.value}</span>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <AdvanceDataTable
        {...tableProps}
        data={result?.items ?? []}
        columns={createOvertimeLedgerColumns({ t })}
        isLoading={isLoading}
        error={error instanceof Error ? error : null}
        pagination={{
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          totalCount: result?.totalCount ?? 0,
        }}
        isExpandRowContent={false}
        mobileView="cards"
      />
    </div>
  );
};
